import { useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/shadcn/card';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@/components/shadcn/radio-group';
import { Textarea } from '@/components/shadcn/textarea';
import {
  govtIntakeFormReporting,
  govtIntakeModuleUploadExistingForms,
  govtIntakeModuleUploadExistingReports,
} from '@/data/govtIntakeStaticData.ts';
import type { ValidationChange } from '@/modules/governmentIntake/base-entity.tsx';
import HeaderAndSub from '@/modules/governmentIntake/components/shared-component.tsx';
import {
  EMPTY_PROPERTIES_PAIN_POINTS,
  type FileData,
  type PropertiesPainPointsData,
  type PropertiesSubNeedsData,
  useIntakeForm,
} from '@/modules/governmentIntake/context/intake-form-context.tsx';
import UploadBox from '../../UploadBox';

const EMPTY_NEEDS_FORM: PropertiesSubNeedsData = {
  automatedReporting: '',
  reportCount: '',
  automatedReportingFile: [],
  programForms: '',
  formCount: '',
  automatedFormFile: [],
};

export default function PropertiesSubScoping({ onValidationChange }: ValidationChange) {
  const { data, updatePropertiesSubSection } = useIntakeForm();
  const needsData = data.properties?.needs ?? EMPTY_NEEDS_FORM;
  const painPointsData = data.properties?.painPoints ?? EMPTY_PROPERTIES_PAIN_POINTS;

  const onValidationChangeRef = useRef(onValidationChange);
  onValidationChangeRef.current = onValidationChange;

  useEffect(() => {
    onValidationChangeRef.current?.(true);
  }, []);

  const updateNeeds = (newData: PropertiesSubNeedsData) => {
    updatePropertiesSubSection('needs', newData);
  };

  const updatePainPoints = (newData: PropertiesPainPointsData) => {
    updatePropertiesSubSection('painPoints', newData);
  };

  const handleNeedsFieldChange = <K extends keyof PropertiesSubNeedsData>(field: K, value: PropertiesSubNeedsData[K]) => {
    updateNeeds({
      ...needsData,
      [field]: value,
    });
  };

  const handlePainFieldChange = <K extends keyof PropertiesPainPointsData>(field: K, value: PropertiesPainPointsData[K]) => {
    updatePainPoints({
      ...painPointsData,
      [field]: value,
    });
  };

  const needsReports = needsData.automatedReporting === 'Yes';
  const needsForms = needsData.programForms === 'Yes';

  return (
    <div className='flex w-full flex-col overflow-y-auto'>
      <div className='w-full max-w-5xl'>
        <header className='pt-4'>
          <HeaderAndSub
            title='Scoping'
            subTitle='Define property reporting needs, digitization, and operational priorities for SEIN Terminal.'
          />
        </header>
        <section className='flex-1 overflow-y-auto pb-4'>
          <div className='grid grid-cols-1 lg:grid-cols-2 py-8 gap-6'>
            <Card className='w-full shadow-none border-gray-300'>
              <CardContent>
                <HeaderAndSub title='What you need from SEIN Terminal' subTitle='Reporting and forms for properties' titleClass='text-lg' />
                <div className='space-y-3 pt-8'>
                  <Label className='font-semibold pt-4'>Do you need automated reporting for properties?</Label>
                  <RadioGroup value={needsData.automatedReporting} onValueChange={(v) => handleNeedsFieldChange('automatedReporting', v)}>
                    {govtIntakeFormReporting.map((item) => (
                      <div key={item.id} className='flex items-center gap-2'>
                        <RadioGroupItem value={item.title} id={`prop-reporting-${item.id}`} />
                        <Label className='cursor-pointer select-none' htmlFor={`prop-reporting-${item.id}`}>
                          {item.title}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>

                  {needsReports && (
                    <Card className='mx-6 p-4 shadow-none'>
                      <div className='space-y-2'>
                        <Input
                          className='text-sm'
                          type='number'
                          placeholder='Enter number of reports'
                          value={needsData.reportCount}
                          onChange={(e) => handleNeedsFieldChange('reportCount', e.target.value)}
                        />
                        {needsData.reportCount ? (
                          <div className='text-sm text-gray-500 mt-4'>
                            <Label>{govtIntakeModuleUploadExistingReports}</Label>
                            <UploadBox
                              files={needsData.automatedReportingFile ?? []}
                              onChange={(files) => handleNeedsFieldChange('automatedReportingFile', files as FileData[])}
                            />
                          </div>
                        ) : null}
                      </div>
                    </Card>
                  )}
                </div>

                <div className='space-y-3 pt-8'>
                  <Label className='font-semibold'>
                    Do you need us to digitize any public or internally facing forms and/or applications?
                  </Label>
                  <RadioGroup value={needsData.programForms} onValueChange={(v) => handleNeedsFieldChange('programForms', v)}>
                    {govtIntakeFormReporting
                      .filter((item) => item.id !== 'notSure')
                      .map((item) => (
                        <div key={item.id} className='flex items-center gap-2'>
                          <RadioGroupItem value={item.title} id={`prop-forms-${item.id}`} />
                          <Label className='cursor-pointer select-none' htmlFor={`prop-forms-${item.id}`}>
                            {item.title}
                          </Label>
                        </div>
                      ))}
                  </RadioGroup>

                  {needsForms && (
                    <Card className='p-4 mx-6 shadow-none'>
                      <div className='space-y-2'>
                        <Input
                          type='number'
                          className='text-sm'
                          placeholder='Enter number of forms'
                          value={needsData.formCount}
                          onChange={(e) => handleNeedsFieldChange('formCount', e.target.value)}
                        />
                        {needsData.formCount ? (
                          <div className='text-sm text-gray-500 mt-4'>
                            <Label>{govtIntakeModuleUploadExistingForms}</Label>
                            <UploadBox
                              files={needsData.automatedFormFile ?? []}
                              onChange={(files) => handleNeedsFieldChange('automatedFormFile', files as FileData[])}
                            />
                          </div>
                        ) : null}
                      </div>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className='w-full shadow-none border-gray-300'>
              <CardContent>
                <HeaderAndSub
                  title='Priority & outcomes'
                  subTitle='What matters most and what does success look like?'
                  titleClass='text-lg'
                  subtitleClass='text-gray-500 pt-1'
                />
                <div className='pt-8'>
                  <Label className='block text-base font-medium text-black mb-2'>Top 3 pain points (ranked)</Label>
                  <div className='space-y-4'>
                    <div className='space-y-1'>
                      <Label className='block text-base font-medium text-black'>1. Highest priority pain point</Label>
                      <Input
                        type='text'
                        className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                        placeholder='e.g., Manual data entry for participant applications'
                        value={painPointsData.painPoint1}
                        onChange={(e) => handlePainFieldChange('painPoint1', e.target.value)}
                      />
                    </div>
                    <div className='space-y-1'>
                      <Label className='block text-base font-medium text-black'>2. Second priority pain point</Label>
                      <Input
                        type='text'
                        className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                        placeholder='e.g., Difficult to track program compliance'
                        value={painPointsData.painPoint2}
                        onChange={(e) => handlePainFieldChange('painPoint2', e.target.value)}
                      />
                    </div>
                    <div className='space-y-1'>
                      <Label className='block text-base font-medium text-black'>3. Third priority pain point</Label>
                      <Input
                        type='text'
                        className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                        placeholder='e.g., Reporting to funders is time-consuming'
                        value={painPointsData.painPoint3}
                        onChange={(e) => handlePainFieldChange('painPoint3', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className='space-y-2 pt-10'>
                    <Label className='block text-base font-medium text-black'>What leadership asks for that is hard to answer</Label>
                    <Textarea
                      rows={4}
                      className='w-full resize-none bg-gray-50 shadow-none border-gray-300'
                      placeholder='Questions or metrics that are requested by difficult with current systems...'
                      value={painPointsData.leadershipAsksFor}
                      onChange={(e) => handlePainFieldChange('leadershipAsksFor', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </div>
  );
}
